import { get, isEqual, omit, uniqueId } from 'lodash';
import { action, computed, observable, toJS } from 'mobx';

import { showSuccessToast } from 'core/components/toast';
import { Collection } from 'core/model/Collection';
import api from 'core/util/api';
import { ModelBase, ModelRequestStatus, UnidentifiedModelType } from 'core/model/model.types';
import { WithOptional } from '@kentik/ui-shared/types/util';

// we never serialize these fields to the API.
const EDITED_CREATED_FIELDS = ['edate', 'cdate'] as const;

export interface DestroyModelOptions<T> {
  toast?: boolean;
  remove?: boolean;
  url?: string;
  data?: T;
  optimistic?: boolean;
}

/**
 * Standard Model class.
 *
 * @typeParam T - should always be defined and represents the interface for this Model if this Model were a simple JSON object.
 * @typeParam D - `keyof defaults()`
 */
export class Model<T extends ModelBase, D extends keyof T = 'id'> {
  @observable.shallow
  public error?: { label: ModelRequestStatus; body?: unknown } | null;

  public optimisticId = uniqueId('i_');

  public attributes: T;

  /**
   * This is listed as public but it's probably best to treat it as protected - accessing collection indirectly like
   * this is pretty funky! Also note that you may need to cast this `as unknown as MyCollection` when you want to use
   * it externally.
   */
  // eslint-disable-next-line no-use-before-define
  public collection: Collection<Model<T, D>> | null = null;

  @observable
  public requestStatus: ModelRequestStatus = null;

  @observable
  protected requestProgress = 0;

  @observable
  public lastUpdated?: number;

  /**
   * Do not read this from outside of Collection/Model!
   */
  public store?: object;

  public readonly fetchMethod?: 'post' | 'get';

  public get urlPaths(): { update: string; fetch: string } | undefined {
    return undefined;
  }

  constructor(attributes?: WithOptional<T, D | 'id'>) {
    /*
     * Very important to note that any fields used here, must be defined with getter syntax
     * in subclasses or they will be undefined here!
     */

    this.attributes = observable.object(
      Object.assign({}, this.defaults, attributes) as T,
      {},
      { deep: false, name: 'Model.attributes' }
    );
  }

  protected get defaults(): Omit<Pick<T, D>, 'id'> {
    return {} as Pick<T, D>;
  }

  /**
   * Return the base url used in the `url` method
   */
  protected get urlRoot(): string | undefined {
    return undefined;
  }

  protected get omitDuringSerialize(): Readonly<(keyof T)[]> {
    return [];
  }

  public get messages(): Partial<Readonly<Record<'create' | 'update' | 'destroy' | 'duplicate', string>>> {
    return {
      create: 'Added successfully',
      update: 'Updated successfully',
      destroy: 'Removed successfully',
      duplicate: 'Duplicated successfully'
    };
  }

  protected get showToastTitles(): boolean {
    return false;
  }

  @computed
  public get index(): number {
    if (!this.collection) {
      throw new Error('index: Model is not a part of a collection');
    }

    return this.collection.models.indexOf(this);
  }

  /**
   * Return the url for this given REST resource
   */
  public get url(): string {
    if (!this.urlRoot && (!this.collection || !this.collection.url)) {
      throw new Error('No `urlRoot` provided');
    }

    const urlRoot = this.urlRoot ?? this.collection?.url;

    if (!urlRoot) {
      // TODO should ideally be a compile-time error
      throw new Error('Model fetch attempted for model with no defined URL!');
    }

    if (this.isNew) {
      return urlRoot;
    }

    return `${urlRoot}/${this.get('id')}`;
  }

  public toDate(this: Model<T, D>, _dateType: 'etime' | 'edate' | 'ctime' | 'cdate'): Date | undefined {
    return undefined;
  }

  @computed
  public get lastEdited(): Date | undefined {
    return this.toDate('etime') || this.toDate('edate');
  }

  @computed
  public get created(): Date | undefined {
    return this.toDate('ctime') || this.toDate('cdate');
  }

  /**
   * Whether the resource is new or not
   *
   * We determine this asking if it contains the `id` attribute (set by the server).
   */
  @computed
  public get isNew(): boolean {
    return !this.has('id') || (!this.get('id') && this.get('id') !== 0);
  }

  /**
   * Determines whether or not this Model is selectable in it's collection.
   */
  @computed
  public get isSelectable(): boolean {
    return true;
  }

  /**
   * Determines whether or not this Model is selected in it's collection.
   */
  @computed
  public get isSelected(): boolean {
    if (!this.collection) {
      console.warn(`BaseModel (${this.constructor.name}) is not a part of a Collection. Cannot call isSelected()`);

      return false;
    }

    const { selected } = this.collection;

    if (Array.isArray(selected)) {
      return (selected as (typeof this)[]).includes(this);
    }

    return this.collection.selected === this;
  }

  /**
   * Get an id from the model. It will use either
   * the backend assigned one or the client.
   */
  public get id(): NonNullable<T['id']> | string {
    if (this.has('id')) {
      // probably this is the only line we need but I've kept this in for possible legacy issues
      return this.get('id') ?? this.optimisticId;
    }
    return this.optimisticId;
  }

  /**
   * Questions whether the request exists and matches a certain label.
   * If no label are passed it will check if any requests are active.
   */
  public isRequestActive = (label?: string): boolean | null => {
    if (!label) {
      return this.requestStatus !== null;
    }

    return (
      (!!this.requestStatus && this.requestStatus === label) ||
      (this.collection && this.collection.isRequestActive(label))
    );
  };

  protected baseGet(this: Model<T, D>): T;

  protected baseGet<K extends keyof T>(this: Model<T, D>, attribute: K): T[K];

  protected baseGet<K extends keyof T>(this: Model<T, D>, attribute?: K): T | T[K] {
    if (!attribute) {
      return toJS(this.attributes);
    }

    if (typeof attribute === 'string' && attribute.includes('.')) {
      const [r, ...rest] = attribute.split('.');
      const root = r as keyof T;
      // ignore dot in keys attributes like `f_hll(inet_src_addr,0.0001)__k_last`
      if (!this.attributes[root] && this.has(attribute)) {
        return this.attributes[attribute];
      }
      if (rest.length === 1) {
        // @ts-expect-error left in for legacy purposes but please do not use this - TS won't let you do it safely!
        return this.attributes[root] && this.attributes[root][rest[0] as keyof T[typeof root]];
      }
      return this.attributes[root] && get(this.attributes[root], rest.join('.'));
    }

    if (Array.isArray(attribute)) {
      return attribute.reduce((result, attr) => {
        if (attr.includes('.')) {
          [attr] = attr.split('.');
        }
        result[attr] = this.attributes[attr as keyof T];
        return result;
      }, {} as Partial<T>);
    }

    return this.attributes[attribute];
  }

  public get(this: Model<T, D>): T;

  public get<K extends string & keyof T>(this: Model<T, D>, attribute: K, defaultValue?: never): T[K];

  public get<K extends string & keyof T, T2>(
    this: Model<T, D>,
    attribute: K,
    defaultValue: T2
  ): Exclude<T[K], undefined | null | false> | T2;

  /**
   * Get the attribute from the model.
   *
   * For optional fields use `has()` to check whether the field is populated.
   * If no attribute is passed, returns all of the model attributes as a plain object.
   */
  public get<K extends string & keyof T>(this: Model<T, D>, attribute?: K, defaultValue?: T[K]): T | T[K] | undefined {
    if (!attribute) {
      return this.baseGet() || defaultValue;
    }
    const result = this.baseGet(attribute);

    return result === undefined ? defaultValue : result;
  }

  /**
   * Returns whether the given field exists for the model.
   */
  public has<K extends keyof T>(this: Model<T, D>, attribute: K): boolean {
    return this.attributes[attribute] !== undefined && this.attributes[attribute] !== null;
  }

  protected get sortValues(): Partial<Record<keyof T, (_model: Model<T, D>) => string | number>> {
    return {};
  }

  public getSortValue(this: Model<T, D>, field: (string & keyof T) | undefined): string | number {
    if (!field) {
      return '';
    }
    let value;

    if (this.sortValues[field]) {
      value = this.sortValues[field](this);
    } else {
      value = this.get(field);
      if (value === undefined) {
        value = this[field as keyof Model<T, D>];
      }
    }

    // Replacing undefined with empty string makes _.orderBy happy when ordering numbers and strings
    return typeof value === 'number' ? value : value?.toString() || '';
  }

  /**
   * Returns a raw JS instance of our Model, used for debugging purposes mostly.
   */
  public toJS(this: Model<T, D>): T {
    return toJS(this.attributes);
  }

  public isEqual(this: Model<T, D>, otherModel: Model<T, D>): boolean {
    return otherModel && otherModel.toJS && isEqual(this.toJS(), otherModel.toJS());
  }

  @action
  public setRequestStatus = (status: ModelRequestStatus): void => {
    this.requestStatus = status;
  };

  /**
   * Set the current selected model in a collection to this model. Typically this
   * method is called from a UI with a direct reference to `this`, so we keep the
   * binding with an arrow function. `onClick={model.select}`
   */
  @action
  public select = (options: { multi?: boolean; shiftKey?: boolean; fetch?: boolean } = {}): Promise<void> => {
    const { multi = false, shiftKey = false, fetch = false } = options;

    if (!this.collection) {
      console.warn(`BaseModel (${this.constructor.name}) is not a part of a Collection. Cannot call select()`);
    }

    if (!this.isSelectable) {
      return Promise.resolve();
    }

    if (fetch) {
      return this.fetch().then(() => {
        this.collection?.select(this, { multi });
      });
    }
    return Promise.resolve().then(() => this.collection?.select(this, { multi, shiftKey }));
  };

  @action
  public deselect = (): void => {
    if (!this.collection) {
      console.warn(`Model (${this.constructor.name}: ${this.id}) is not a part of a Collection. Cannot call select()`);
    }

    if (!this.isSelected) {
      console.warn(`Model (${this.constructor.name}: ${this.id}) is not selected.`);
    }

    this.collection?.clearSelection();
  };

  @action
  public setLastUpdated = (): void => {
    this.lastUpdated = Date.now();
  };

  /**
   * Merge the given data with the current attributes.
   *
   * Does NOT merge `undefined` values as this can break type safety! Unfortunately there
   * is not an easy way to define a Partial type which does not allow "defined undefined" values.
   *
   * One day we might want to convert the regular `set()` method to follow this pattern but if so
   * then we can just do a find & replace.
   */
  @action
  public safeSet(this: Model<T, D>, data: Partial<T>): Model<T, D> {
    for (const [key, value] of Object.entries(data)) {
      if (value !== undefined) {
        this.attributes[key as keyof T] = value;
      }
    }
    this.setLastUpdated();

    return this;
  }

  /**
   * Merge the given data with the current attributes
   * @deprecated see `this.safeSet(data)`
   */
  public set(this: Model<T, D>, data: Partial<T>): Model<T, D>;

  public set<K extends string & keyof T>(this: Model<T, D>, fieldName: K, fieldData: T[K]): Model<T, D>;

  /**
   * Merge the given data with the current attributes
   */
  @action
  public set<K extends string & keyof T & D>(
    this: Model<T, D>,
    dataOrFieldName: K | Partial<T> = {},
    fieldData?: T[K]
  ): Model<T, D> {
    if (typeof dataOrFieldName === 'string' && fieldData != null) {
      this.attributes[dataOrFieldName] = fieldData;
    } else {
      Object.assign(this.attributes, dataOrFieldName);
    }

    this.setLastUpdated();

    return this;
  }

  /**
   * Replaces all of the attributes with new ones.
   */
  @action
  public replace = (data: object = {}): void => {
    // @ts-expect-error difficult to get typing in here right now, needs reworking
    this.attributes = observable.object(data, {}, { deep: false, name: 'Model.attributes' });

    this.setLastUpdated();
  };

  @action
  public duplicate = (options: { removeId?: boolean; save?: boolean; deserialize?: boolean } = {}): Model<T, D> => {
    const { removeId = true, save = true, deserialize = true } = options;
    const attributes = (removeId ? omit(this.get(), ['id']) : this.get()) as T;

    if (!this.collection) {
      return new Model<T, D>(attributes);
    }

    const dupe = this.collection.build(attributes, { deserialize });

    if (save) {
      dupe.save(attributes);
    }

    return dupe;
  };

  /**
   * Saves the resource on the backend.
   *
   * If the item has an `id` it updates it, otherwise it creates the new resource.
   *
   * It supports optimistic and patch updates.
   *
   * @param attributes
   * @param options
   * @param options.optimistic - updates the model before we get a response back from the server.
   * @param options.patch - uses `PATCH` versus `PUT`.
   */
  @action
  public async save(
    this: Model<T, D>,
    attributes: Partial<T>,
    options: {
      optimistic?: boolean;
      patch?: boolean;
      preserialize?: boolean;
      setOnPatchSuccess?: boolean;
      clearSelection?: boolean;
      toast?: boolean;
      sendProvidedValuesOnly?: boolean;
      toastMessage?: string;
      url?: string;
    } = {}
  ): Promise<void | T> {
    const {
      optimistic = false,
      patch = false,
      preserialize = true,
      setOnPatchSuccess = true,
      clearSelection = true,
      toast = true,
      toastMessage,
      sendProvidedValuesOnly = false,
      url
    } = options;

    this.requestStatus = 'updating';
    this.requestProgress = 0;
    if (this.isNew) {
      this.set(Object.assign({}, attributes));
      return this.create(attributes, {
        optimistic,
        toast,
        clearSelection,
        url
      });
    }
    let data: Partial<T> = attributes;
    const originalAttributes = this.get();

    // if PATCH, we only want to send the values that changed.
    if (patch) {
      data = Object.keys(attributes).reduce((result, att) => {
        const attribute = att as string & keyof T;
        const newValue = attributes[attribute];
        const oldValue = originalAttributes[attribute];
        if (newValue !== oldValue) {
          // @ts-expect-error TODO this is pretty unsafe and can result in accidental data deletion! but also very hard to rewrite
          result[attribute] = newValue;
        }
        return result;
      }, {} as T);
    } else if (!sendProvidedValuesOnly) {
      data = Object.assign({}, originalAttributes, attributes);
    }

    if (optimistic) {
      this.set(attributes);
    }

    const saveUrl = url || (this.urlPaths ? this.urlPaths.update : this.url);

    const apiMethod = patch ? 'patch' : 'put';
    const promise = api[apiMethod](saveUrl, {
      data: preserialize ? this.serialize(data) : data
    });

    return promise.then(
      (success) => {
        if (this.collection && clearSelection) {
          this.collection.clearSelection();
        }

        if (!patch || setOnPatchSuccess) {
          this.set(this.deserialize(success));
        }

        if (toast) {
          showSuccessToast(toastMessage || this.messages.update, {
            title: this.showToastTitles ? 'Updated' : undefined
          });
        }

        if (this.collection) {
          this.collection.setLastUpdated();
        }

        this.setLastUpdated();

        this.requestStatus = null;

        return success;
      },
      (error) => {
        if (optimistic) {
          this.set(originalAttributes);
        }
        this.error = { label: 'updating' };
        this.requestStatus = null;
        throw error;
      }
    );
  }

  /**
   * Saves this new model on the backend (via POST operation)
   */
  @action
  public async create(
    this: Model<T, D>,
    attributes = {},
    options: { optimistic?: boolean; url?: string; clearSelection?: boolean; toast?: boolean } = {}
  ): Promise<T> {
    const label = 'creating';
    const data = Object.assign({}, this.get(), attributes);
    const { clearSelection, toast = true } = options;

    const url = options.url || (this.collection && this.collection.url ? this.collection.url : this.urlRoot);
    if (!url) {
      throw new Error('Attempted to save Model with no defined URL');
    }
    const payload = this.serialize(data);
    const promise = api.post(url, { data: payload });

    this.requestStatus = label;

    return promise.then(
      (success: T) => {
        this.set(this.deserialize(success));

        if (this.collection) {
          this.collection.add([this]);

          if (clearSelection) {
            this.collection.clearSelection();
            this.collection.sort(); // Note: it seemed to make sense not to re-apply existing filter rules
          }
        }

        if (toast) {
          const createMessage = this.messages ? this.messages.create : 'Created Successfully';
          showSuccessToast(createMessage, {
            title: this.showToastTitles ? 'Created' : undefined
          });
        }

        this.requestStatus = null;

        return success;
      },
      (error: unknown) => {
        this.error = { label, body: error };
        this.requestStatus = null;
        throw error;
      }
    );
  }

  @action
  public async fetch(this: Model<T, D>, options = {}): Promise<void | T> {
    const label = 'fetching';
    this.requestStatus = label;
    this.requestProgress = 0;

    const url = this.urlPaths ? this.urlPaths.fetch : this.url;
    const apiFetchMethod = this.fetchMethod === 'post' ? api.post : api.get;
    const promise = apiFetchMethod(url, options);

    return promise.then(
      (success: T) => {
        this.set(this.deserialize(success));
        this.setRequestStatus(null);
        return success;
      },
      (error) => {
        this.error = { label, body: error };
        this.requestStatus = null;
        throw error;
      }
    );
  }

  @action
  public destroyModel = async (options: DestroyModelOptions<T> = {}): Promise<unknown> => {
    const { toast = true, remove = true, url, data } = options;
    const destroyUrl =
      url || (this.collection?.urlPaths?.destroy ? `${this.collection.urlPaths.destroy}/${this.get('id')}` : this.url);

    this.requestStatus = 'destroying';

    return api.del(destroyUrl, data ? { data } : undefined).then(
      action((success: unknown) => {
        if (toast) {
          showSuccessToast(this.messages.destroy, {
            title: this.showToastTitles ? 'Removed' : undefined
          });
        }

        if (this.collection) {
          if (remove) {
            this.collection.remove([this.id.toString()]);
          }

          this.collection.clearSelection();
        }

        this.requestStatus = null;

        return success;
      }),
      action((error) => {
        console.error('destroyModel error', error);
        if (options.optimistic && this.collection) {
          this.collection.add([this.get()]);
        }
        this.error = { label: 'destroying', body: error };
        this.requestStatus = null;

        return error;
      })
    );
  };

  /**
   * Destroys the resource on the client and requests the backend to delete it there too
   */
  public async destroy(this: Model<T, D>, options: DestroyModelOptions<T> = {}): Promise<unknown> {
    if (this.collection) {
      if (this.isNew || !this.urlRoot) {
        return Promise.resolve(this.collection.remove([this.id.toString()]));
      }
    }
    return this.destroyModel(options);
  }

  public serialize(this: Model<T, D>, data?: Partial<T>): Partial<T> {
    return omit(data || this.get(), [...this.omitDuringSerialize, ...EDITED_CREATED_FIELDS]) as Partial<T>;
  }

  protected get jsonRoot(): string | undefined {
    return undefined;
  }

  public deserialize(
    this: Model<T, D>,
    data: WithOptional<T, D> | Record<string, WithOptional<T, D>>
  ): WithOptional<T, D> {
    return data && this.jsonRoot
      ? (data as Record<string, WithOptional<T, D>>)[this.jsonRoot]
      : (data as WithOptional<T, D>);
  }

  public getUniqueCopyName(this: Model<T, D>, nameField: string & keyof T): string {
    let name: string = this.get(nameField)?.toString() || '';

    if (this.collection) {
      // Remove the [Copy] or [Copy x] from the name
      const nameMatch = name.match(/\[Copy.*\] (.+)/);
      if (nameMatch) {
        name = nameMatch[nameMatch.length - 1];
      }

      if (!this.collection.find({ [nameField]: `[Copy] ${name}` })) {
        return `[Copy] ${name}`;
      }
      for (let i = 1; ; i += 1) {
        if (
          !this.collection.find({
            [nameField]: `[Copy ${i}] ${name}`
          })
        ) {
          return `[Copy ${i}] ${name}`;
        }
      }
    }

    return name;
  }
}

/**
 * A concrete, non-generic version of the Model class that can be
 * easily instantiated from JavaScript files.
 *
 * @deprecated for future code it is generally preferable to create your own type-safe Model
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export default class GenericModel extends Model<UnidentifiedModelType, any> {}
